import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, MenuItem, Paper, Tab, Table, TableBody, TableCell, TableHead, TableRow, Tabs, TextField, Typography,
} from '@mui/material';
import { useToast } from '../../../context/ToastContext';
import { AuthService } from '../../../services/auth.service';
import { PortalActivity, PortalActivityFilter } from '../../../services/portalAdmin.service';
import {
  SupplierOneTimeLink, SupplierPortalAdminService, SupplierPortalContact, SupplierPortalSettings,
} from '../../../services/supplierPortalAdmin.service';
import ScrollableTable from '../../common/ScrollableTable';
import { formatDateTime } from '../../../utils/dateUtils';
import { humanizeEnum } from '../../../utils/format';

// ---- F8 supplier portal: the "Portal" tab on the supplier page ----
const EVENTS = [
  'LOGIN_OK', 'LOGIN_FAILED', 'LOGIN_LOCKED', 'LOGOUT', 'INVITE_CREATED', 'RESET_CREATED', 'INVITE_ACCEPTED', 'PASSWORD_RESET',
  'PASSWORD_CHANGED', 'CONTACT_DISABLED', 'CONTACT_ENABLED', 'UNLOCKED', 'PO_LIST', 'PO_VIEW', 'PDF_DOWNLOAD',
  'PO_ACKNOWLEDGED', 'BILL_REQUEST_SUBMITTED', 'BILL_REQUEST_WITHDRAWN', 'BILL_REQUEST_COMMENTED', 'BILL_REQUEST_REJECTED',
  'BILL_REQUEST_CONVERTED',
];
const STATUS_COLOR: Record<string, 'success' | 'warning' | 'default'> = { ACTIVE: 'success', INVITED: 'warning', DISABLED: 'default' };
const isLocked = (c: SupplierPortalContact) => !!c.lockedUntil && new Date(c.lockedUntil).getTime() > Date.now();

/** The link is returned once (only its hash is stored), so this dialog is the only chance to copy it. */
export const SupplierLinkDialog: React.FC<{ link: SupplierOneTimeLink; onClose: () => void }> = ({ link, onClose }) => {
  const toast = useToast();
  const copy = async () => {
    try { await navigator.clipboard.writeText(link.url); toast.showSuccess('Link copied'); }
    catch { toast.showError('Could not copy; select the link and copy it by hand'); }
  };
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth data-help-page="procurement.portalLink.page">
      <DialogTitle>Link for {link.contact.name}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          This link is shown once and works for one use. It expires {formatDateTime(link.expiresAt)}.
        </Typography>
        <TextField data-help="procurement.portalLink.field.url" fullWidth multiline minRows={2} value={link.url} InputProps={{ readOnly: true }} aria-label="One-time link"
          onFocus={e => e.target.select()} />
        {link.emailed
          ? <Alert severity="success" sx={{ mt: 2 }}>Also e-mailed to {link.emailedTo ?? link.contact.email}</Alert>
          : <Alert severity="info" sx={{ mt: 2 }}>Not e-mailed. Copy the link and send it to the supplier yourself.</Alert>}
      </DialogContent>
      <DialogActions>
        <Button data-help="procurement.portalCard.action.copy" variant="contained" onClick={copy}>Copy</Button>
        <Button data-help="procurement.portalLink.close" onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

const SupplierPortalCard: React.FC<{ supplierId: string }> = ({ supplierId }) => {
  const institutionId = AuthService.getSelectedInstitution() as string;
  const toast = useToast();
  const [tab, setTab] = useState(0);
  const [settings, setSettings] = useState<SupplierPortalSettings | null>(null);
  const [contacts, setContacts] = useState<SupplierPortalContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [link, setLink] = useState<SupplierOneTimeLink | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteName, setInviteName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [alsoEmail, setAlsoEmail] = useState(false);
  const [deactivating, setDeactivating] = useState<SupplierPortalContact | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState<PortalActivityFilter>({});
  const [activity, setActivity] = useState<PortalActivity[]>([]);
  const [actLoading, setActLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [s, c] = await Promise.all([
        SupplierPortalAdminService.getSettings(institutionId), SupplierPortalAdminService.contacts(institutionId, supplierId),
      ]);
      setSettings(s); setContacts(c);
    } catch (e) {
      toast.showApiError(e, 'Failed to load supplier portal access');
    } finally {
      setLoading(false);
    }
  }, [institutionId, supplierId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const loadActivity = useCallback(async () => {
    try {
      setActLoading(true);
      setActivity((await SupplierPortalAdminService.activity(institutionId, supplierId, filter)).rows);
    } catch (e) {
      toast.showApiError(e, 'Failed to load portal activity');
    } finally {
      setActLoading(false);
    }
  }, [institutionId, supplierId, filter]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (tab === 1) loadActivity(); }, [tab, loadActivity]);

  const act = async (fn: () => Promise<unknown>, ok: string, fail: string) => {
    setBusy(true);
    try { await fn(); toast.showSuccess(ok); await load(); }
    catch (e) { toast.showApiError(e, fail); }
    finally { setBusy(false); }
  };
  const linkAct = async (fn: () => Promise<SupplierOneTimeLink>, fail: string) => {
    setBusy(true);
    try { setLink(await fn()); await load(); }
    catch (e) { toast.showApiError(e, fail); }
    finally { setBusy(false); }
  };
  const invite = () => linkAct(async () => {
    const r = await SupplierPortalAdminService.invite(institutionId, supplierId,
      { name: inviteName.trim(), email: inviteEmail.trim(), sendEmail: alsoEmail });
    setInviteOpen(false); setInviteName(''); setInviteEmail(''); setAlsoEmail(false);
    return r;
  }, 'Failed to invite the contact');
  const setF = (patch: Partial<PortalActivityFilter>) => setFilter(f => ({ ...f, ...patch }));

  const off = settings && (!settings.serverConfigured || !settings.supplierPortalEnabled);
  return (
    <Paper sx={{ p: 2, mb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h6">Supplier portal access</Typography>
        <Button data-help="procurement.portalCard.invite" variant="outlined" size="small" disabled={!!off || busy} onClick={() => setInviteOpen(true)}>Invite contact</Button>
      </Box>
      {settings && !settings.serverConfigured && (
        <Alert severity="warning" sx={{ mt: 1 }}>The portal is not configured on this server.</Alert>
      )}
      {settings && settings.serverConfigured && !settings.supplierPortalEnabled && (
        <Alert severity="info" sx={{ mt: 1 }}>The supplier portal is switched off. Switch it on in Procurement settings.</Alert>
      )}
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 1 }}>
        <Tab label="Contacts" /><Tab label="Activity" />
      </Tabs>

      {tab === 0 && (loading ? <CircularProgress /> : (
        <ScrollableTable>
          <Table size="small" data-help-section="procurement.portalCard.contacts">
            <TableHead><TableRow>
              <TableCell data-help="procurement.portalCard.col.name">Name</TableCell><TableCell data-help="procurement.portalCard.col.email">E-mail</TableCell><TableCell data-help="procurement.portalCard.col.status">Status</TableCell>
              <TableCell data-help="procurement.portalCard.col.login">Last login</TableCell><TableCell data-help="procurement.portalCard.col.locked">Locked until</TableCell><TableCell data-help="procurement.portalCard.col.actions" />
            </TableRow></TableHead>
            <TableBody>
              {contacts.length === 0 && <TableRow><TableCell colSpan={6} align="center">No portal contacts yet.</TableCell></TableRow>}
              {contacts.map(c => (
                <TableRow key={c.id}>
                  <TableCell>{c.name}</TableCell><TableCell>{c.email}</TableCell>
                  <TableCell><Chip size="small" label={humanizeEnum(c.status)} color={STATUS_COLOR[c.status] ?? 'default'} /></TableCell>
                  <TableCell>{c.lastLoginAt ? formatDateTime(c.lastLoginAt) : '-'}</TableCell>
                  <TableCell>{isLocked(c) ? formatDateTime(c.lockedUntil as string) : '-'}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {c.status === 'INVITED' && (
                      <Button data-help="procurement.portalCard.reinvite" size="small" disabled={busy} onClick={() => linkAct(() => SupplierPortalAdminService.reinvite(institutionId, c.id), 'Failed to create the invite link')}>New invite link</Button>
                    )}
                    {c.status === 'ACTIVE' && (
                      <Button data-help="procurement.portalCard.reset" size="small" disabled={busy} onClick={() => linkAct(() => SupplierPortalAdminService.resetLink(institutionId, c.id), 'Failed to create the reset link')}>New reset link</Button>
                    )}
                    {isLocked(c) && (
                      <Button data-help="procurement.portalCard.unlock" size="small" disabled={busy} onClick={() => act(() => SupplierPortalAdminService.unlock(institutionId, c.id), 'Contact unlocked', 'Failed to unlock')}>Unlock</Button>
                    )}
                    {c.status !== 'DISABLED' ? (
                      <Button data-help="procurement.portalCard.deactivate" size="small" color="error" disabled={busy} onClick={() => { setReason(''); setDeactivating(c); }}>Deactivate</Button>
                    ) : (
                      <Button data-help="procurement.portalCard.reactivate" size="small" disabled={busy} onClick={() => act(() => SupplierPortalAdminService.reactivate(institutionId, c.id), 'Contact reactivated', 'Failed to reactivate')}>Reactivate</Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </ScrollableTable>
      ))}

      {tab === 1 && (
        <Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }} data-help-section="procurement.portalCard.filter">
            <TextField data-help="procurement.portalCard.field.contact" select size="small" label="Contact" value={filter.contactId ?? ''} sx={{ flex: '1 1 200px', maxWidth: 280 }}
              onChange={e => setF({ contactId: e.target.value })}>
              <MenuItem value="">All</MenuItem>
              {contacts.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </TextField>
            <TextField data-help="procurement.portalCard.field.event" select size="small" label="Event" value={filter.event ?? ''} sx={{ flex: '1 1 200px', maxWidth: 280 }}
              onChange={e => setF({ event: e.target.value })}>
              <MenuItem value="">All</MenuItem>
              {EVENTS.map(ev => <MenuItem key={ev} value={ev}>{humanizeEnum(ev)}</MenuItem>)}
            </TextField>
            <TextField data-help="procurement.portalCard.field.from" size="small" type="date" label="From" value={filter.from ?? ''} InputLabelProps={{ shrink: true }}
              sx={{ flex: '1 1 150px', maxWidth: 200 }} onChange={e => setF({ from: e.target.value })} />
            <TextField data-help="procurement.portalCard.field.to" size="small" type="date" label="To" value={filter.to ?? ''} InputLabelProps={{ shrink: true }}
              sx={{ flex: '1 1 150px', maxWidth: 200 }} onChange={e => setF({ to: e.target.value })} />
          </Box>
          {actLoading ? <CircularProgress /> : (
            <ScrollableTable><Table size="small" data-help-section="procurement.portalCard.activity">
              <TableHead><TableRow><TableCell data-help="procurement.portalCard.act.when">When</TableCell><TableCell data-help="procurement.portalCard.act.event">Event</TableCell><TableCell data-help="procurement.portalCard.act.contact">Contact</TableCell><TableCell data-help="procurement.portalCard.act.doc">Document</TableCell><TableCell data-help="procurement.portalCard.act.ip">IP</TableCell><TableCell data-help="procurement.portalCard.act.detail">Detail</TableCell></TableRow></TableHead>
              <TableBody>
                {activity.length === 0 && <TableRow><TableCell colSpan={6} align="center">No activity.</TableCell></TableRow>}
                {activity.map(a => (
                  <TableRow key={a.id}>
                    <TableCell>{formatDateTime(a.occurredAt)}</TableCell><TableCell>{humanizeEnum(a.event)}</TableCell>
                    <TableCell>{a.contactName ?? '-'}</TableCell><TableCell>{a.documentType ? humanizeEnum(a.documentType) : '-'}</TableCell>
                    <TableCell>{a.ip ?? '-'}</TableCell><TableCell>{a.detail ?? ''}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table></ScrollableTable>
          )}
        </Box>
      )}

      {inviteOpen && (
        <Dialog open onClose={() => !busy && setInviteOpen(false)} maxWidth="xs" fullWidth data-help-page="procurement.portalInvite.page">
          <DialogTitle>Invite supplier contact</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <TextField data-help="procurement.portalInvite.field.name" label="Name" required fullWidth value={inviteName} inputProps={{ maxLength: 150 }} onChange={e => setInviteName(e.target.value)} />
              <TextField data-help="procurement.portalInvite.field.email" label="E-mail" type="email" required fullWidth value={inviteEmail} inputProps={{ maxLength: 254 }} onChange={e => setInviteEmail(e.target.value)} />
              <FormControlLabel
                control={<Checkbox data-help="procurement.portalInvite.field.alsoEmail" checked={alsoEmail} disabled={!settings?.emailEnabled} onChange={(_, c) => setAlsoEmail(c)} />}
                label={settings?.emailEnabled ? 'Also e-mail the invite link' : 'Also e-mail the invite link (portal e-mail is off)'} />
              <Typography variant="caption" color="text.secondary">
                The invite link is shown once after you invite, so you can copy it. Nothing is e-mailed unless you tick the box.
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button data-help="procurement.portalInvite.cancel" onClick={() => setInviteOpen(false)} disabled={busy}>Cancel</Button>
            <Button data-help="procurement.portalCard.action.inviteConfirm" variant="contained" disabled={busy || !inviteName.trim() || !inviteEmail.includes('@')} onClick={invite}>Invite</Button>
          </DialogActions>
        </Dialog>
      )}
      {deactivating && (
        <Dialog open onClose={() => setDeactivating(null)} maxWidth="xs" fullWidth data-help-page="procurement.portalDeactivate.page">
          <DialogTitle>Deactivate {deactivating.name}?</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 2 }}>Their sessions end now and they cannot sign in until you reactivate them.</Typography>
            <TextField data-help="procurement.portalDeactivate.field.reason" label="Reason" required fullWidth multiline minRows={2} value={reason} inputProps={{ maxLength: 300 }} onChange={e => setReason(e.target.value)} />
          </DialogContent>
          <DialogActions>
            <Button data-help="procurement.portalDeactivate.cancel" onClick={() => setDeactivating(null)}>Cancel</Button>
            <Button data-help="procurement.portalCard.action.deactivateConfirm" color="error" variant="contained" disabled={busy || !reason.trim()} onClick={async () => {
              const c = deactivating;
              await act(() => SupplierPortalAdminService.deactivate(institutionId, c.id, reason.trim()), 'Contact deactivated', 'Failed to deactivate');
              setDeactivating(null);
            }}>Deactivate</Button>
          </DialogActions>
        </Dialog>
      )}
      {link && <SupplierLinkDialog link={link} onClose={() => setLink(null)} />}
    </Paper>
  );
};

export default SupplierPortalCard;
// ---- end F8 ----
